<template>
    <div class="auth-modal" v-show="isVisible">
        <div class="login-form-container">
            <h2>Reset Password</h2>

            <div v-if="step === 1">
                <p>Enter your email to receive a 6-digit verification code.</p>
                <div class="form-group">
                    <label>Email:</label>
                    <input 
                        v-model="email" 
                        type="email" 
                        placeholder="email@example.com"
                        required
                    />
                </div>
                <div class="button-row">
                    <button 
                        @click="handleSendCode" 
                        class="user-modal-submit-btn green" 
                        :disabled="loading"
                    >
                        {{ loading ? 'Sending...' : 'Send Reset Code' }}
                    </button>
                    <button @click="$emit('cancel')" class="user-modal-cancel-btn">
                        Cancel
                    </button>
                </div>
            </div>

            <div v-if="step === 2">
                <p>Enter the 6-digit code sent to <strong>{{ email }}</strong> and choose a new password.</p>
                
                <div class="form-group">
                    <label>Verification Code:</label>
                    <input 
                        v-model="verificationCode" 
                        type="text" 
                        placeholder="123456"
                        maxlength="6"
                        autocomplete="one-time-code"
                    />
                </div>

                <div class="form-group">
                    <label>New Password:</label>
                    <div class="password-wrapper">
                        <input 
                            v-model="newPassword" 
                            :type="showPassword ? 'text' : 'password'" 
                            placeholder="Minimum 8 characters"
                            autocomplete="new-password"
                        />
                        <button 
                            type="button" 
                            class="toggle-eye" 
                            @click="showPassword = !showPassword" 
                            tabindex="-1"
                        >
                            {{ showPassword ? '👁️' : '👁️‍🗨️' }}
                        </button>
                    </div>
                </div>

                <div class="form-group">
                    <label>Confirm Password:</label>
                    <input 
                        v-model="confirmPassword" 
                        :type="showPassword ? 'text' : 'password'" 
                        placeholder="Repeat new password"
                        autocomplete="new-password"
                    />
                </div>

                <div class="button-row">
                    <button 
                        @click="handleConfirmReset" 
                        class="user-modal-submit-btn green" 
                        :disabled="loading"
                    >
                        {{ loading ? 'Updating...' : 'Update Password' }}
                    </button>
                    <button @click="step = 1" class="user-modal-cancel-btn">
                        Back
                    </button>
                </div>
            </div>

            <div v-if="localError" class="error-message" style="margin-top: 15px;">
                {{ localError }}
            </div>
        </div>
    </div>
</template>

<script>
import RequestPasswordResetMutation from '../graphql/user/RequestPasswordResetMutation.gql';
import ConfirmPasswordResetMutation from '../graphql/user/ConfirmPasswordResetMutation.gql';

export default {
    name: 'ForgotPasswordModal',
    props: {
        isVisible: Boolean,
        initialEmail: String
    },
    data() {
        return {
            step: 1,
            email: this.initialEmail || '',
            verificationCode: '',
            newPassword: '',
            confirmPassword: '',
            showPassword: false,
            loading: false,
            localError: ''
        };
    },
    methods: {
        async handleSendCode() {
            this.loading = true;
            this.localError = '';
            try {
                await this.$apollo.mutate({
                    mutation: RequestPasswordResetMutation,
                    variables: { email: this.email }
                });
                this.step = 2;
                this.verificationCode = ''; // Reset code in case of retry
            } catch (err) {
                this.localError = err.message || 'Error sending code.';
            } finally {
                this.loading = false;
            }
        },
        async handleConfirmReset() {
            if (!this.verificationCode || !this.newPassword || !this.confirmPassword) {
                this.localError = 'Please fill in all fields.';
                return;
            }
            if (this.newPassword !== this.confirmPassword) {
                this.localError = 'Passwords do not match.';
                return;
            }
            if (this.newPassword.length < 8) {
                this.localError = 'Password must be at least 8 characters long.';
                return;
            }

            this.loading = true;
            this.localError = '';
            try {
                await this.$apollo.mutate({
                    mutation: ConfirmPasswordResetMutation,
                    variables: {
                        email: this.email,
                        code: this.verificationCode.trim(),
                        newPassword: this.newPassword
                    }
                });
                this.$emit('success');
            } catch (err) {
                console.error('Reset error:', err);
                this.localError = err.message || 'Error resetting password.';
            } finally {
                this.loading = false;
            }
        }
    },
    watch: {
        isVisible(newVal) {
            if (newVal) {
                this.localError = '';
                // If we don't have an email, we MUST be at step 1
                if (!this.email) {
                    this.step = 1;
                }
            }
        },
        initialEmail: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.email = newVal;
                }
            }
        }
    },
    mounted() {
        // Removed auto-send logic to prevent UI flash and unwanted emails
    }

};
</script>

<style src="@/assets/user-shared.css"></style>
<style scoped>
    .instruction-text {
        margin-bottom: 1rem;
        font-size: 0.9rem;
        color: #555;
    }
</style>
